/**
 * Picking an image out of a paste or a drop, and naming it for the media store.
 *
 * Pure so it can be tested without a DOM: clipboard and drag events are awkward to synthesise, and
 * the interesting behaviour (which item wins, what the file ends up called) has nothing to do with
 * the event plumbing.
 */

/** Extensions Anki's media store and the API's content-type map both understand. */
const EXTENSION: Record<string, string> = {
  "image/png": "png",
  "image/jpeg": "jpg",
  "image/gif": "gif",
  "image/webp": "webp",
  "image/bmp": "bmp",
  "image/svg+xml": "svg",
};

export function extensionFor(mimeType: string): string {
  return EXTENSION[mimeType.toLowerCase()] ?? "png";
}

/**
 * A stable, sortable name. Screenshots arrive nameless from the clipboard, and Anki's media folder
 * is one flat namespace shared with every other deck — an unprefixed `image.png` would collide with
 * something eventually. The server renames on collision anyway, but only after the fact.
 */
export function mediaFilename(mimeType: string, stamp: number, slot: string): string {
  return `nihongonator-${stamp}-${slot}.${extensionFor(mimeType)}`;
}

/**
 * The first image in a clipboard or drag payload.
 *
 * A screenshot pasted from the OS usually arrives as one `image/*` item, but a copy from a browser
 * carries the image AND its `text/html` and `text/uri-list` representations, so "first item" is not
 * good enough — it has to be the first item that is actually an image.
 */
export function firstImage(files: ArrayLike<File> | null | undefined): File | null {
  if (!files) return null;
  for (let i = 0; i < files.length; i += 1) {
    const file = files[i];
    if (file && file.type.startsWith("image/")) return file;
  }
  return null;
}

/** Which slot a paste means: plain Ctrl+V is Image, Ctrl+Shift+V is Extra image. */
export function slotForPaste(shiftKey: boolean): "image" | "extraImage" {
  return shiftKey ? "extraImage" : "image";
}

/**
 * The note field a slot writes to.
 *
 * The slots are named for the sentence they hang off; the note's fields are named by the notetype,
 * and the two casings are not the same. One mapping, because a card's slots and a sentence's slots
 * must never disagree about which image is the "extra" one.
 */
export function fieldForSlot(slot: "image" | "extraImage"): "Image" | "ExtraImage" {
  return slot === "extraImage" ? "ExtraImage" : "Image";
}

/**
 * The slot's mark in a stored filename, for `mediaFilename`'s third argument.
 *
 * Beside `fieldForSlot` for the same reason: Anki's media folder is one flat namespace, and three
 * surfaces write into it. Spelled out in each of them, the mapping was three independent
 * statements — two keyed on the slot and one on the note field, so they could not even be compared
 * by eye.
 */
export function suffixForSlot(slot: "image" | "extraImage"): "img" | "extra" {
  return slot === "extraImage" ? "extra" : "img";
}

/**
 * The filename inside an Anki image field value, or null if there isn't one.
 *
 * A slot holds the *field value* — `<img src="vn-1.png">` — because that is what a mined card
 * inherits verbatim. Everything that renders one has to take it apart again to build a URL, and
 * everything that moves one between slots has to name the file it is moving.
 */
export function parseImageFilename(value: string): string | null {
  // Double-quoted, single-quoted, or bare — the three shapes this collection actually holds, and
  // deliberately the same three the server's `_IMG_PATTERN` accepts.
  //
  // **This used to take double quotes only, and that silently hid 139 cards.** They carry
  // `<img src=ToradoraEP01mkv440.199441.909.webp>` — no quotes, written by a 2021 video-mining
  // script through AnkiConnect. Anki renders them (a bare attribute value is valid HTML); Read and
  // Cards rendered nothing, because with no filename there is no `<img>` to build and not even a
  // failed request to notice. Invisible in the one way that leaves no trace.
  //
  // The bare branch stops at whitespace or `>` the way HTML does, and excludes `"'=<` and a
  // backtick too, so a malformed tag cannot turn the rest of the field into a filename.
  const match = /<img[^>]*src\s*=\s*(?:"([^"]*)"|'([^']*)'|([^\s"'=<>`]+))/i.exec(value);
  if (!match) return null;
  return match[1] ?? match[2] ?? match[3] ?? null;
}

/**
 * The inverse: an image field value pointing at `filename`.
 *
 * Beside `parseImageFilename` deliberately. They are inverses, they are used by different surfaces,
 * and a round-trip test can only assert that if it can see both — which it could not while one of
 * them was a private function in `Read.tsx` and the other did not exist.
 */
export function wrapImageValue(filename: string): string {
  return `<img src="${filename}">`;
}
